import {
  FACILITY_DASHBOARD_DATA,
  FETCH_FACILITY_TB_DASHBOARD,
  FETCH_FACILITY_TB_TEST_TYPE_DASHBOARD,
  FETCH_GLOBAL_DASHBOARD,
  FETCH_PH_VERIFIED_REPORT,
  FETCH_PRIORITY_TEST_TYPE_DASHBOARD,
  FETCH_PROGRAM_DASHBOARD,
  REPORT_DATA,
  REPORT_TEST_TYPE_CONFIG,
  SELECTED_REPORT_TEST_TYPE,
  SET_REPORT_PAGINATION,
} from "constants/ActionTypes";
import { httpClient } from "utils/Api";
import { pageSizeOptions } from "utils/Constant";
import { programEnum } from "utils/Helpers";
import { fetchError, fetchStart, fetchSuccess } from "./Common";

const dispatchPagination = (dispatch, response) => {
  if (response.data?.count !== undefined) {
    dispatch({
      type: SET_REPORT_PAGINATION,
      payload: {
        count: response.data.count,
        page: response.data.page,
        page_size: response.data.page_size,
        total_pages: response.data.total_pages,
        next: response.data.next,
        previous: response.data.previous,
      },
    });
  }
};

export const fetchPHVerifiedReport = (id, program, test_type, facility) => {
  const _params = `/verified_ph_sample_result/?test_order_status=${id}&facility=${facility}&program__name=${program}`;
  return async (dispatch) => {
    dispatch(fetchStart());
    await httpClient
      .get(_params)
      .then((res) => {
        dispatch(fetchSuccess());
        dispatch({ type: FETCH_PH_VERIFIED_REPORT, payload: res.data });
      })
      .catch((error) => {
        dispatch(fetchError(error[1]?.message));
        throw error;
      });
  };
};

export const fetchProgramDashboard = (program, periodicity) => {
  let _params = `/facility_program_dashboard/?periodicity=${periodicity}&program__name=${program}`;
  return async (dispatch) => {
    dispatch(fetchStart());
    await httpClient
      .get(_params)
      .then((res) => {
        dispatch(fetchSuccess());
        dispatch({ type: FETCH_PROGRAM_DASHBOARD, payload: res.data });
      })
      .catch((error) => {
        dispatch(fetchError(error[1]?.message));
        throw error;
      });
  };
};
export const fetchProgramTestTypeDashboard = (
  program,
  periodicity,
  currentTest,
) => {
  let _params = `/facility_program_test_type_dashboard/?periodicity=${periodicity}&program__name=${program}&test_type=${
    currentTest ? currentTest : 0
  }`;
  return async (dispatch) => {
    dispatch(fetchStart());
    await httpClient
      .get(_params)
      .then((res) => {
        dispatch(fetchSuccess());
        dispatch({ type: FETCH_PROGRAM_DASHBOARD, payload: res.data });
      })
      .catch((error) => {
        dispatch(fetchError(error[1]?.message));
        throw error;
      });
  };
};

export const fetchFacilityTBDashboard = (periodicity) => {
  let _params = `/tb_dashboard/?periodicity=${periodicity}`;
  return async (dispatch) => {
    dispatch(fetchStart());
    await httpClient
      .get(_params)
      .then((res) => {
        dispatch(fetchSuccess());
        dispatch({ type: FETCH_FACILITY_TB_DASHBOARD, payload: res.data });
      })
      .catch((error) => {
        dispatch(fetchError(error[1]?.message));
        throw error;
      });
  };
};

export const fetchFacilityTBTestTypeDashboard = (periodicity, currentTest) => {
  let _params = `/tb_test_type_dashboard/?periodicity=${periodicity}&test_type=${
    currentTest ? currentTest : 0
  }`;
  return async (dispatch) => {
    dispatch(fetchStart());
    await httpClient
      .get(_params)
      .then((res) => {
        dispatch(fetchSuccess());
        dispatch({
          type: FETCH_FACILITY_TB_TEST_TYPE_DASHBOARD,
          payload: res.data,
        });
      })
      .catch((error) => {
        dispatch(fetchError(error[1]?.message));
        throw error;
      });
  };
};

import { API_URL } from "utils/Constant";

const buildDashboardQuery = ({
  date_field,
  scope,
  program,
  test_type,
  states,
  lga,
  facility,
  method,
  reporting_state,
  reporting_lga,
  start_date,
  end_date,
}) => {
  const query = new URLSearchParams();

  const append = (key, value) => {
    if (value !== undefined && value !== null && value !== "") {
      query.append(key, value);
    }
  };

  append("date_field", date_field);
  append("scope", scope);
  append("program", program);
  append("test_type", test_type);
  append("states", states);
  append("lga", lga);
  append("facility", facility);
  append("method", method);
  append("reporting_state", reporting_state);
  append("reporting_lga", reporting_lga);
  append("start_date", start_date);
  append("end_date", end_date);

  return query.toString();
};

const makeDashboardThunk = (path, actionType) => (params) => {
  const _params = `${path}?${buildDashboardQuery(params)}`;

  return async (dispatch) => {
    dispatch(fetchStart());
    await httpClient
      .get(_params)
      .then((res) => {
        dispatch(fetchSuccess());
        dispatch({
          type: actionType,
          payload: res.data,
        });
      })
      .catch((error) => {
        dispatch(fetchError(error[1]?.message));
        throw error;
      });
  };
};

export const fetchGlobalDashboard = makeDashboardThunk(
  API_URL.DASHBOARD.GLOBAL_DASHBOARD,
  FETCH_GLOBAL_DASHBOARD,
);

export const fetchPriorityTestTypeDashboard = makeDashboardThunk(
  API_URL.DASHBOARD.TIER_PROGRAM,
  FETCH_PRIORITY_TEST_TYPE_DASHBOARD,
);

// NOTE: deliberately dispatches FETCH_PRIORITY_TEST_TYPE_DASHBOARD, not a
// General-specific action type — both program endpoints feed the same
// `priorityDashboardList` slice that TestsContent reads. Preserved from the
// original. If a separate slice is ever wanted, this is the line to change.
export const fetchGeneralDashboard = makeDashboardThunk(
  API_URL.DASHBOARD.TIER_GENERAL,
  FETCH_PRIORITY_TEST_TYPE_DASHBOARD,
);
/**
 * @function getReportData
 * @description - This function is used to get the report data
 */

export const getReportData = ({
  program,
  test_type,
  test_status,
  start_date,
  end_date,
  reporting_state,
  reporting_lga,
  search,
  ordering,
  page = 1,
  pageSize = pageSizeOptions[1],
  date_field,
}) => {
  const _params = {
    test_type,
    start_date,
    end_date,
    test_order_status: test_status,
    program,
    reporting_state,
    reporting_lga,
    search,
    ordering,
    page,
    page_size: pageSize,
    date_field,
  };

  const endpointMapping = {
    [programEnum.TB]: "tb_report/",
    [programEnum.GENERAL]: "general_order_report/",
    default: "general_program_report/",
  };

  const endpoint = endpointMapping[program] || endpointMapping.default;

  return async (dispatch) => {
    dispatch({ type: REPORT_DATA, payload: [] });
    try {
      dispatch(fetchStart());
      const res = await httpClient.get(endpoint, { params: _params });
      dispatch(fetchSuccess());
      dispatch({
        type: REPORT_DATA,
        payload: res.data?.data || res.data,
      });
      dispatchPagination(dispatch, res);
    } catch (error) {
      dispatch(fetchError(error[1]?.message));
      throw error;
    }
  };
};

/**
 * @function getFacilityDashboardData
 * @description - This function is used to get the facility dashboard data
 */

export const getFacilityDashboardData = ({
  program,
  test_type,
  start_date,
  end_date,
  date_field,
}) => {
  const params = {
    test_type,
    start_date,
    end_date,
    program,
    date_field,
  };

  let endpoint;
  switch (program) {
    case programEnum.TB:
      endpoint = "facility_tb_dashboard/";
      break;
    case programEnum.GENERAL:
      endpoint = "facility_general_order_dashboard/";
      break;
    default:
      endpoint = "facility_general_program_dashboard/";
  }

  return async (dispatch) => {
    try {
      dispatch(fetchStart());
      const res = await httpClient.get(endpoint, { params });
      dispatch(fetchSuccess());
      dispatch({
        type: FACILITY_DASHBOARD_DATA,
        payload: res.data,
      });
    } catch (error) {
      dispatch(fetchError(error[1]?.message));
      throw error;
    }
  };
};

/**
 * @function setReportTestTypeConfig
 * @description - This function is used to set the report test type config
 */

export const setReportTestTypeConfig = (config) => {
  return {
    type: REPORT_TEST_TYPE_CONFIG,
    payload: config,
  };
};

export const selectedReportTestType = (test_type) => {
  return {
    type: SELECTED_REPORT_TEST_TYPE,
    payload: test_type,
  };
};
